<template>
  <div class="col-12" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
        <div class="bg-danger shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ $t('salary_management.add_deduction') }}
            </h6>
          </div>
        </div>
      </div>

      <div class="card-body px-0 pb-2">
        <div class="p-4">
          <form @submit.prevent="submit" @keydown.enter.prevent>
            <div class="row g-3">

              <div class="col-md-6">
                <label class="bmd-label-float">{{ $t('salary_management.employee_type') }} *</label>
                <select class="form-select borderless-input" :class="{ 'is-invalid': errors.modelType }"
                  v-model="form.modelType" @change="loadEmployees" required>
                  <option value="">{{ $t('salary_management.select_type') }}</option>
                  <option value="teacher">{{ $t('salary_management.teachers') }}</option>
                  <option value="employee">{{ $t('salary_management.employees') }}</option>
                  <option value="user">{{ $t('salary_management.users') }}</option>
                </select>
                <div class="invalid-feedback">{{ errors.modelType }}</div>
              </div>

              <div class="col-md-6">
                <label class="bmd-label-float">{{ $t('salary_management.employee') }} *</label>
                <select class="form-select borderless-input" :class="{ 'is-invalid': errors.modelId }"
                  v-model="form.modelId" :disabled="!form.modelType" required>
                  <option value="">{{ $t('salary_management.select_employee') }}</option>
                  <option v-for="e in employees" :key="e.id" :value="e.id">{{ e.name }}</option>
                </select>
                <div class="invalid-feedback">{{ errors.modelId }}</div>
              </div>

              <div class="col-md-6">
                <label class="bmd-label-float">{{ $t('salary_management.value') }} *</label>
                <input type="number" step="0.01" class="form-control borderless-input"
                  :class="{ 'is-invalid': errors.value }" v-model.number="form.value" required />
                <div class="invalid-feedback">{{ errors.value }}</div>
              </div>

              <div class="col-md-6">
                <label class="bmd-label-float">{{ $t('salary_management.payment_date') }} *</label>
                <input type="date" class="form-control borderless-input"
                  :class="{ 'is-invalid': errors.payment_date }" v-model="form.payment_date" required />
                <div class="invalid-feedback">{{ errors.payment_date }}</div>
              </div>

              <div class="col-12">
                <label class="bmd-label-float">{{ $t('salary_management.description') }} *</label>
                <textarea class="form-control borderless-input" rows="3"
                  :placeholder="$t('salary_management.deduction_description_placeholder')"
                  v-model.trim="form.description" :class="{ 'is-invalid': errors.description }" required></textarea>
                <div class="invalid-feedback">{{ errors.description }}</div>
              </div>

            </div>
          </form>

          <div class="d-flex gap-3 mt-4">
            <button type="button" class="btn btn-danger btn-sm flex-grow-1" @click="submit" :disabled="submitting">
              <span v-if="submitting"><i class="fas fa-spinner fa-spin me-2"></i>{{ $t('common.saving') }}</span>
              <span v-else><i class="fas fa-minus-circle me-2"></i>{{ $t('salary_management.add_deduction') }}</span>
            </button>
            <button type="button" class="btn btn-outline-secondary btn-sm flex-grow-1" @click="goBack" :disabled="submitting">
              <i class="fas fa-times me-2"></i>{{ $t('common.cancel') }}
            </button>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import api from '@/api';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const toast = useToast();

const submitting = ref(false);
const employees = ref([]);

const form = ref({
  modelType: '',
  modelId: '',
  value: null,
  payment_date: new Date().toISOString().split('T')[0],
  description: ''
});
const errors = ref({});

const toNumber = (v) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const cleaned = String(v).replace(/[^\d.-]/g, '');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const NON_PAYROLL_ROLES = ['teacher', 'student', 'guardian'];

const loadEmployees = async () => {
  employees.value = [];
  form.value.modelId = '';
  if (!form.value.modelType) return;

  try {
    if (form.value.modelType === 'user') {
      const res = await api.get('/users');
      const list = (Array.isArray(res.data) ? res.data : []).filter(
        (u) => !NON_PAYROLL_ROLES.includes(u.role?.slug)
      );
      employees.value = list.map((u) => ({
        id: u.id,
        name: u.name || u.email || `#${u.id}`,
      }));
      return;
    }

    const endpoint = form.value.modelType === 'teacher' ? '/teachers' : '/employees';
    const res = await api.get(endpoint, { params: { per_page: 1000 } });
    const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);

    employees.value = list.map(emp => ({
      id: emp.id,
      name: emp.username || emp.name || emp.email || `#${emp.id}`,
    }));
  } catch (_) {
    employees.value = [];
    toast.error(t('common.error'));
  }
};

const validate = () => {
  errors.value = {};
  if (!form.value.modelType) errors.value.modelType = t('validation.required');
  if (!form.value.modelId) errors.value.modelId = t('validation.required');
  if (!form.value.value || toNumber(form.value.value) <= 0) errors.value.value = t('validation.positive_number');
  if (!form.value.payment_date) errors.value.payment_date = t('validation.required');
  if (!form.value.description) errors.value.description = t('validation.required');
  return Object.keys(errors.value).length === 0;
};

const submit = async () => {
  if (!validate()) return;
  submitting.value = true;
  try {
    const { modelType, modelId, ...payload } = form.value;
    payload.value = toNumber(payload.value);
    await store.dispatch('salaryManagement/addDeduction', { modelType, modelId, data: payload });
    toast.success(t('salary_management.deduction_added'));
    router.push({ name: 'salary.deductionsList' });
  } catch (e) {
    toast.error(e?.response?.data?.message || t('salary_management.deduction_error'));
  } finally {
    submitting.value = false;
  }
};

const goBack = () => router.push({ name: 'salary.deductionsList' });
</script>
